import { SlotStatus } from "../../../../../common/enums";
import { isBookableStatus, isBlockedStatus, isWaitlistStatus } from "../../../../../common/utilities/slotStatusUtility";
import { IGroupClassTimeSlot, IGroupClassDateAvailability } from "../../models";
import { IService } from "../../../appointment/models/IService";
import { IEmployeeAvailability } from "../../../appointment/models/IEmployee";

interface IAppointmentSlot {
  time: string;
  isAvailable: boolean;
  employeeId: number;
  eventId: number;
  slotStatus: SlotStatus;
  disabledReason?: string;
  availableSpaces?: number;
  isCancelled?: boolean;
}

interface IAppointmentAvailability {
  date: Date;
  dateKey: string;
  hasSlots: boolean;
  timeSlots: IAppointmentSlot[];
}

/**
 * Transforms a single group class slot to appointment format
 */
export const transformGroupClassSlot = (slot: IGroupClassTimeSlot): IAppointmentSlot => {
  // Map GroupClassSlotStatus to SlotStatus by numeric value
  // Available (0), WaitlistAvailable (1), Blocked (2)
  const slotStatus: SlotStatus = slot.status as number as SlotStatus;

  // Slot is clickable if Available or WaitlistAvailable (not Blocked / not cancelled)
  // and if not all family members have reservation
  const isClickable =
    !slot.isCancelled &&
    isBookableStatus(slotStatus) &&
    !slot.allFamilyMembersHaveReservation;

  // Determine disabled reason — a cancelled class wins over every other reason.
  let disabledReason: string | undefined;
  if (slot.isCancelled) {
    disabledReason = "Класът няма да се проведе";
  } else if (slot.allFamilyMembersHaveReservation) {
    disabledReason = "Вече имате резервация за този час";
  } else if (isBlockedStatus(slotStatus)) {
    disabledReason = slot.statusMessage || "Запълнено - няма места";
  }

  return {
    time: slot.time,
    isAvailable: isClickable,
    employeeId: slot.instructorId,
    eventId: slot.eventId,
    slotStatus: slotStatus,
    disabledReason,
    availableSpaces: slot.availableSpaces,
    isCancelled: slot.isCancelled,
  };
};

/**
 * Transforms group class availability map to appointment format
 */
export const transformDateAvailabilityMap = (
  dateAvailabilityMap: Map<string, IGroupClassDateAvailability>
): Map<string, IAppointmentAvailability> => {
  const transformedMap = new Map<string, IAppointmentAvailability>();

  dateAvailabilityMap.forEach((availability, dateKey) => {
    const appointmentSlots = availability.slots.map(transformGroupClassSlot);

    transformedMap.set(dateKey, {
      date: availability.date,
      dateKey,
      hasSlots: appointmentSlots.length > 0,
      timeSlots: appointmentSlots,
    });
  });

  return transformedMap;
};

/**
 * Checks if any slots are available in the date availability map
 */
export const hasAnyAvailableSlots = (
  dateAvailabilityMap: Map<string, IGroupClassDateAvailability>
): boolean => {
  return Array.from(dateAvailabilityMap.values()).some(
    (availability) => availability.hasAvailableSlots
  );
};

/**
 * Finds and returns the first available date after today
 */
export const findFirstAvailableDate = (
  dateAvailabilityMap: Map<string, IGroupClassDateAvailability>
): Date | null => {
  const sortedDateKeys = Array.from(dateAvailabilityMap.keys()).sort();
  const todayKey = new Date().toISOString().split("T")[0];

  for (const dateKey of sortedDateKeys) {
    if (dateKey <= todayKey) continue;

    const availability = dateAvailabilityMap.get(dateKey);
    if (availability?.hasAvailableSlots) {
      return new Date(dateKey + "T12:00:00");
    }
  }

  return null;
};

/**
 * Gets the selected slot from availability map by time (returns first match)
 */
export const getSelectedSlot = (
  dateAvailabilityMap: Map<string, IGroupClassDateAvailability>,
  selectedDate: Date,
  selectedTime: string
): IGroupClassTimeSlot | null => {
  const dateKey = selectedDate.toISOString().split("T")[0];
  const availability = dateAvailabilityMap.get(dateKey);

  if (!availability) return null;

  return availability.slots.find((slot) => slot.time === selectedTime) || null;
};

/**
 * Gets a slot from availability map by eventId (handles duplicate times correctly)
 */
export const getSelectedSlotByEventId = (
  dateAvailabilityMap: Map<string, IGroupClassDateAvailability>,
  eventId: number
): IGroupClassTimeSlot | null => {
  for (const availability of dateAvailabilityMap.values()) {
    const slot = availability.slots.find((s) => s.eventId === eventId);
    if (slot) return slot;
  }
  return null;
};

/**
 * Creates a dummy service object for compatibility with appointment wizard
 */
export const createDummyService = (selectedClassName: string | undefined): IService | null => {
  if (!selectedClassName) return null;

  return {
    id: 0,
    name: selectedClassName,
    description: "",
    duration: 60,
    price: 0,
    minPrice: 0,
    maxPrice: 0,
    isActive: true,
    categoryId: null,
    sortOrder: 0,
  };
};

/**
 * Determines the instructor display name based on selection state
 */
export const getInstructorDisplayName = (
  instructorId: number,
  instructorName: string | undefined,
  selectedDate: Date | null,
  selectedTime: string | null,
  dateAvailabilityMap: Map<string, IGroupClassDateAvailability>,
  selectedEventId?: number | null,
): string => {
  let displayName = instructorName || "";

  // If "any instructor" (-1) is selected
  if (instructorId === -1) {
    // If no time selected yet, show prompt
    if (!selectedTime || !selectedDate) {
      displayName = "Моля изберете час";
    } else {
      // Use eventId lookup when available to correctly resolve duplicate times
      const selectedSlot = selectedEventId != null
        ? getSelectedSlotByEventId(dateAvailabilityMap, selectedEventId)
        : getSelectedSlot(dateAvailabilityMap, selectedDate, selectedTime);
      displayName = selectedSlot?.instructorName || "Всеки инструктор";
    }
  }

  return displayName;
};

/**
 * Creates a dummy employee object for compatibility with appointment wizard
 */
export const createDummyEmployee = (
  instructorId: number | undefined,
  displayName: string
): IEmployeeAvailability | null => {
  if (!instructorId) return null;

  return {
    id: instructorId,
    name: displayName,
    role: "",
    isAvailable: true,
    priceModifier: 1,
    bookedSlots: [],
  };
};

/**
 * Checks if the selected slot is a waitlist booking
 */
export const isWaitlistBooking = (slot: IGroupClassTimeSlot): boolean => {
  return isWaitlistStatus(slot.status);
};
